/* Hallmark · genre: atmospheric · macrostructure: 04 Stat-Led · route: custom (tuned)
 * Campaign landing page for paid ad traffic. Deliberately outside design.md, which
 * scopes itself to the signed-in host area and says so ("ActiveAdmin, the auth pages,
 * and the home landing page are out of scope here"). Palette, type, and structure are
 * this page's own; the tokens still come from the repo's brand kit, so the ad and the
 * product look like the same company.
 *
 * The page has exactly one job: start an account. Every link on it advances the funnel,
 * opens a theme demo (itself a tracked ViewContent — see MARKETING_FACT.md), or is a
 * legal document an ad platform requires to be reachable. spec/requests/landing_spec.rb
 * enforces that list; there is no site nav and no marketing footer to wander off through.
 *
 * Two motion primitives only — fade-up on section entry, lift on a theme card — both
 * collapsed by useReducedMotion() and by the reduced-motion block in host_ui.css.
 */
import React from "react";
import { useContent } from "@thoughtbot/superglue";
import { motion, useReducedMotion, type Variants } from "motion/react";
import { ArrowRightIcon, ArrowUpRightIcon } from "lucide-react";

import { AuthConsentNotice } from "@javascript/components/AuthConsentNotice";
import { BrandMark } from "@javascript/components/BrandMark";
import { GoogleAuthButton } from "@javascript/components/GoogleAuthButton";
import { Button } from "@javascript/components/ui/button";
import { cn } from "@javascript/components/utils";

type ThemeFact = {
  key: string;
  name: string;
  category: string | null;
  imagePath: string;
  imageWidth: number;
  imageHeight: number;
  demoPath: string;
};

type LandingContent = {
  hideSharedHeader?: boolean;
  signUpPath: string;
  googleAuthPath?: string;
  copyrightYear: number;
  price?: string;
  themes: ThemeFact[];
};

// One line each, in the couple's language rather than the theme's own art direction.
// Keyed by theme key so a catalog rename surfaces as a missing card, not wrong copy.
const THEME_LINE: Record<string, string> = {
  sakura: "Cherry blossom falls as your guests read. A branch they can touch.",
  love: "Candlelight, deep wine, and a handwritten hand. Your story, scrolled.",
  elegant: "A monogrammed card in brass and forest green, printed for the screen.",
};

// Every row is a property of the product a reader can check on the page itself.
// No invented metrics live here, and none belong here.
const INCLUDED: { term: string; value: string }[] = [
  { term: "Guests", value: "No limit. Twenty or two thousand, same link, same price." },
  { term: "Renewal", value: "None. It is not a subscription and it does not expire." },
  { term: "Credits", value: "None. Nothing to top up before you can send an invitation." },
  { term: "Branding", value: "None. Your names on the page, never ours." },
  { term: "Ads", value: "None. No advertising or tracking scripts run on the page your guests open." },
  { term: "Search", value: "Your invitation is kept out of search results. The link still works for everyone you send it to." },
  { term: "Phone", value: "Built phone-first. Guests save the date, open directions, and RSVP with no app or account." },
  { term: "Themes", value: "Choose from the full curated catalogue. The three above are the featured collection." },
];

// Four denials, each one a competitor's business model. Scannable beats a
// paragraph here: it sits under the button, where a reader checks their doubts.
const DENIALS = ["No subscription", "No charge per guest", "No credits", "No branding"];

const STEPS: { title: string; body: string }[] = [
  { title: "Choose a theme", body: "Open the live demos and choose the one that feels like your day." },
  { title: "Fill one guided form", body: "Add your names, events, story, photos, guest details, and RSVP." },
  { title: "Preview everything", body: "Check the same mobile page your guests will open. Everything before publishing is free." },
  { title: "Publish and share", body: "Pay once, share one link, and follow every reply from your dashboard." },
];

const fadeUp = (reduceMotion: boolean | null): Variants => ({
  hidden: { opacity: 0, y: reduceMotion ? 0 : 16 },
  shown: { opacity: 1, y: 0 },
});

function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="shown"
      viewport={{ once: true, margin: "-10%" }}
      variants={fadeUp(reduceMotion)}
      transition={{ duration: reduceMotion ? 0.01 : 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

const eyebrowClass =
  "text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-[color:var(--cn-accent)]";

const primaryCtaClass =
  "h-13 rounded-full bg-[color:var(--cn-accent)] px-7 text-base font-semibold text-[color:var(--cn-accent-ink)] hover:bg-[color:var(--cn-accent-2)] focus-visible:ring-[color:var(--cn-focus)]";

function PrimaryCta({
  signUpPath,
  children,
  className,
}: {
  signUpPath: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Button asChild size="lg" className={cn(primaryCtaClass, className)}>
      <a href={signUpPath} data-campaign-cta>
        {children}
        <ArrowRightIcon aria-hidden className="ml-1 size-4" />
      </a>
    </Button>
  );
}

/* N5 · Floating pill. Detached from the page edges, blurred over the canvas, and
   sticky — so the one action on this page is never more than a thumb away. The
   wordmark is not a link: linking it home is the exit this page exists to remove. */
function FloatingPill({ signUpPath }: { signUpPath: string }) {
  return (
    <div className="pointer-events-none sticky top-0 z-30 px-3 pt-3 sm:px-4 sm:pt-4">
      <div className="pointer-events-auto mx-auto flex w-full max-w-2xl items-center justify-between gap-3 rounded-full border border-[color:var(--cn-rule)] bg-[color:oklch(0.17_0.011_80_/_0.72)] py-2 pl-4 pr-2 backdrop-blur-md sm:pl-5">
        <BrandMark size="sm" className="text-[color:var(--cn-ink)]" />
        <Button
          asChild
          size="sm"
          className="h-10 shrink-0 rounded-full bg-[color:var(--cn-accent)] px-4 text-sm font-semibold text-[color:var(--cn-accent-ink)] hover:bg-[color:var(--cn-accent-2)] focus-visible:ring-[color:var(--cn-focus)]"
        >
          <a href={signUpPath} data-campaign-cta>
            Start free
          </a>
        </Button>
      </div>
    </div>
  );
}

/* The hero figure. The price is the page's whole argument, so it is the largest
   thing on it — but it never stands alone: the headline beside it says what the
   number buys. The value is quoted from the catalog, never written into the copy. */
function PriceFigure({ price }: { price?: string }) {
  if (!price) return null;

  return (
    <div className="flex flex-col gap-2">
      <p className="flex flex-wrap items-baseline gap-3">
        <span className="font-serif text-xl font-medium leading-none tracking-tight text-[color:var(--cn-muted)] tabular-nums line-through decoration-2 sm:text-2xl">
          US$39
        </span>
        <span className="font-serif text-[2.6rem] font-semibold leading-none tracking-tight text-[color:var(--cn-ink)] tabular-nums sm:text-6xl">
          {price}
        </span>
      </p>
      <p className="text-2xl font-bold uppercase tracking-tight text-[color:var(--cn-accent)] sm:text-3xl">
        51% off
      </p>
      <p className="text-sm text-[color:var(--cn-muted)]">
        Once, launch price — here for a limited time.
      </p>
    </div>
  );
}

function ThemeCover({ theme, className }: { theme: ThemeFact; className?: string }) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border border-[color:var(--cn-rule)] bg-[color:var(--cn-paper-2)] shadow-[0_28px_64px_-32px_oklch(0_0_0/0.9)]",
        className,
      )}
    >
      <img
        src={theme.imagePath}
        alt={`The ${theme.name} theme as a guest sees it`}
        width={theme.imageWidth}
        height={theme.imageHeight}
        decoding="async"
        className="h-full w-full object-cover object-top"
      />
      <span aria-hidden className="campaign-cover-fade absolute inset-0" />
    </div>
  );
}

function Hero({
  signUpPath,
  googleAuthPath,
  price,
  themes,
}: {
  signUpPath: string;
  googleAuthPath?: string;
  price?: string;
  themes: ThemeFact[];
}) {
  const reduceMotion = useReducedMotion();
  const stage = fadeUp(reduceMotion);
  const staged = (index: number) => ({
    initial: "hidden" as const,
    animate: "shown" as const,
    variants: stage,
    transition: {
      duration: reduceMotion ? 0.01 : 0.6,
      delay: reduceMotion ? 0 : index * 0.08,
      ease: [0.16, 1, 0.3, 1] as const,
    },
  });

  return (
    <section className="relative overflow-hidden">
      <div className="mx-auto grid w-full max-w-6xl items-center gap-12 px-4 pb-14 pt-6 sm:px-6 sm:pb-20 sm:pt-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:gap-12">
        <div>
          <motion.p {...staged(0)} className={eyebrowClass}>
            Digital wedding invitations
          </motion.p>

          <motion.div {...staged(1)} className="mt-4 sm:mt-5">
            <PriceFigure price={price} />
          </motion.div>

          <motion.h1
            {...staged(2)}
            className="mt-3 max-w-[22ch] font-serif text-[1.95rem] font-semibold leading-[1.06] tracking-tight text-[color:var(--cn-accent-deep)] text-balance sm:mt-4 sm:text-[3.5rem] dark:text-[color:var(--cn-accent-2)]"
            style={{ overflowWrap: "anywhere" }}
          >
            Your whole wedding.
            <br />
            One beautiful link.
          </motion.h1>

          <motion.p
            {...staged(3)}
            className="mt-4 max-w-md text-base leading-relaxed text-[color:var(--cn-muted)] sm:mt-5 sm:text-lg"
          >
            Share every event, venue, dress code, travel detail, and registry in a
            mobile-first invitation. Guests save the date, open directions, and RSVP
            without an app or account.
          </motion.p>

          <motion.div {...staged(4)} className="mt-5 max-w-md sm:mt-6">
            <div className="flex max-w-sm flex-col gap-3">
              <PrimaryCta signUpPath={signUpPath} className="w-full">
                Build your invitation free
              </PrimaryCta>
              <GoogleAuthButton
                authPath={googleAuthPath}
                className="border-[color:var(--cn-rule)] bg-transparent text-[color:var(--cn-ink)] shadow-none hover:bg-[color:var(--cn-paper-3)] hover:text-[color:var(--cn-ink)] focus-visible:ring-[color:var(--cn-focus)]"
              />
            </div>
            <p className="mt-4 text-sm text-[color:var(--cn-muted)]">
              Build free. Pay once, only when you publish.
            </p>
            <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-[color:var(--cn-muted)]">
              {DENIALS.map((denial) => (
                <li key={denial} className="flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="size-1 rounded-full bg-[color:var(--cn-accent)]"
                  />
                  {denial}
                </li>
              ))}
            </ul>
            {googleAuthPath ? (
              <div className="[&_a]:text-[color:var(--cn-ink)] [&_a]:decoration-[color:var(--cn-rule)] [&_p]:mt-2 [&_p]:text-xs [&_p]:text-[color:var(--cn-muted)]">
                <AuthConsentNotice />
              </div>
            ) : null}
          </motion.div>
        </div>

        <motion.div {...staged(5)} className="lg:pl-4">
          <div className="campaign-fan -mx-1 flex snap-x snap-mandatory items-start gap-3 overflow-x-auto px-1 pb-4 lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-4 lg:overflow-visible lg:px-0 lg:pb-10">
            {themes.map((theme) => (
              <ThemeCover
                key={theme.key}
                theme={theme}
                className="aspect-[3/4] w-[62%] shrink-0 snap-center lg:w-auto"
              />
            ))}
          </div>
          <p className="mt-1 text-center text-xs text-[color:var(--cn-muted)] lg:mt-2">
            Real pages, captured as a guest sees them.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* F6 · Product card grid. Three covers side by side is what a couple comparing
   themes actually wants; the demo link on each is also the funnel's only tracked
   mid-point, so it opens in a new tab and leaves this page standing. */
function ThemeShelf({ themes }: { themes: ThemeFact[] }) {
  return (
    <section
      id="themes"
      className="border-t border-[color:var(--cn-rule-2)] bg-[color:oklch(0.15_0.011_80_/_0.6)]"
    >
      <div className="mx-auto w-full max-w-6xl px-4 py-18 sm:px-6 sm:py-22">
        <Reveal className="max-w-2xl">
          <p className={eyebrowClass}>Open one</p>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-[color:var(--cn-ink)] sm:text-4xl">
            These are live invitations, not screenshots.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-[color:var(--cn-muted)]">
            Every demo below is the real thing, running the same code your guests will open.
            Scroll it, tap the RSVP, hold a photo. Then decide.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {themes.map((theme, index) => (
            <Reveal key={theme.key} delay={index * 0.07}>
              <article className="campaign-card group relative flex h-full flex-col overflow-hidden rounded-2xl">
                <span aria-hidden className="campaign-card-wash pointer-events-none absolute inset-0" />
                <div className="relative aspect-[3/4] overflow-hidden border-b border-[color:var(--cn-rule-2)]">
                  <img
                    src={theme.imagePath}
                    alt={`The ${theme.name} theme as a guest sees it`}
                    width={theme.imageWidth}
                    height={theme.imageHeight}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-top"
                  />
                  <span aria-hidden className="campaign-cover-fade absolute inset-0" />
                </div>
                <div className="relative flex flex-1 flex-col p-5">
                  <h3 className="font-serif text-2xl font-semibold text-[color:var(--cn-ink)]">
                    {theme.name}
                  </h3>
                  {theme.category ? (
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--cn-accent)]">
                      {theme.category}
                    </p>
                  ) : null}
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[color:var(--cn-muted)]">
                    {THEME_LINE[theme.key]}
                  </p>
                  <a
                    href={theme.demoPath}
                    target="_blank"
                    rel="noreferrer"
                    className="campaign-focus mt-5 inline-flex min-h-11 items-center gap-1.5 self-start text-sm font-semibold text-[color:var(--cn-accent)] underline-offset-4 hover:underline"
                  >
                    See {theme.name} live
                    <ArrowUpRightIcon aria-hidden className="size-4" />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* F3 · Tabular spec sheet, two columns, a rule on every row. This replaces the
   four icon-topped feature cards it used to be: a spec sheet reads as a fact
   list a buyer can check, which is what removes a price objection. */
function WhatItCovers({ price, signUpPath }: { price?: string; signUpPath: string }) {
  return (
    <section className="border-t border-[color:var(--cn-rule-2)]">
      <div className="mx-auto w-full max-w-5xl px-4 py-18 sm:px-6 sm:py-22">
        <Reveal>
          <p className={eyebrowClass}>The whole bill</p>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-[color:var(--cn-ink)] sm:text-4xl">
            {price ? `What ${price} covers, and what it does not.` : "What the one payment covers."}
          </h2>
        </Reveal>

        <Reveal delay={0.08}>
          <dl className="mt-10 border-t border-[color:var(--cn-rule)]">
            {INCLUDED.map(({ term, value }) => (
              <div
                key={term}
                className="grid gap-1 border-b border-[color:var(--cn-rule)] py-5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-8 sm:py-6"
              >
                <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--cn-accent)] sm:pt-1">
                  {term}
                </dt>
                <dd className="font-serif text-lg leading-snug text-[color:var(--cn-ink)] sm:text-xl">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </Reveal>

        <Reveal delay={0.12}>
          <p className="mt-8 text-sm text-[color:var(--cn-muted)]">
            Charged once, at the moment you publish. Everything before that is free.
          </p>
          <PrimaryCta signUpPath={signUpPath} className="mt-6">
            Start building free
          </PrimaryCta>
        </Reveal>
      </div>
    </section>
  );
}

/* F4 · Step sequence. Three steps, numbered 01–03, stacked in one column so the
   numeral and its step never split across a gutter on a phone. */
function HowItGoes() {
  return (
    <section className="border-t border-[color:var(--cn-rule-2)] bg-[color:oklch(0.15_0.011_80_/_0.6)]">
      <div className="mx-auto w-full max-w-4xl px-4 py-18 sm:px-6 sm:py-22">
        <Reveal>
          <p className={eyebrowClass}>How it goes</p>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-[color:var(--cn-ink)] sm:text-4xl">
            From theme to sent.
          </h2>
        </Reveal>

        <ol className="mt-10 space-y-px">
          {STEPS.map((step, index) => (
            <Reveal key={step.title} delay={index * 0.07}>
              <li className="flex items-baseline gap-5 border-t border-[color:var(--cn-rule)] py-6 sm:gap-8">
                <span className="font-serif text-2xl font-semibold tabular-nums text-[color:var(--cn-accent-deep)] sm:text-3xl">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="min-w-0">
                  <h3 className="font-serif text-xl font-semibold text-[color:var(--cn-ink)] sm:text-2xl">
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--cn-muted)] sm:text-base">
                    {step.body}
                  </p>
                </span>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

function FinalCta({
  signUpPath,
  googleAuthPath,
  price,
}: {
  signUpPath: string;
  googleAuthPath?: string;
  price?: string;
}) {
  return (
    <section className="border-t border-[color:var(--cn-rule-2)]">
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-20 text-center sm:px-6 sm:py-24">
        <Reveal className="w-full">
          <h2 className="font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-[color:var(--cn-ink)] text-balance sm:text-5xl">
            Start with Sakura, Love, or Elegant.
          </h2>
          <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-[color:var(--cn-muted)] sm:text-lg">
            {price ? `${price} once, when you publish.` : "One payment, when you publish."} Free
            until then, and free to change your mind.
          </p>
          <div className="mx-auto mt-9 flex w-full max-w-sm flex-col gap-3">
            <PrimaryCta signUpPath={signUpPath} className="w-full">
              Create your invitation
            </PrimaryCta>
            <GoogleAuthButton
              authPath={googleAuthPath}
              className="border-[color:var(--cn-rule)] bg-transparent text-[color:var(--cn-ink)] shadow-none hover:bg-[color:var(--cn-paper-3)] hover:text-[color:var(--cn-ink)] focus-visible:ring-[color:var(--cn-focus)]"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* Ft5 · Statement. One closing sentence, then a hairline meta row. The link
   columns and social profiles that used to live here were the page's biggest
   leak; Terms and Privacy stay because an ad platform must be able to reach them. */
function StatementFooter({ copyrightYear }: { copyrightYear: number }) {
  const legalLinkClass =
    "campaign-focus text-[color:var(--cn-muted)] underline-offset-4 transition-colors hover:text-[color:var(--cn-ink)] hover:underline";

  return (
    <footer
      data-campaign-footer
      className="border-t border-[color:var(--cn-rule)] bg-[color:oklch(0.15_0.011_80_/_0.75)]"
    >
      <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <p className="max-w-[38ch] font-serif text-2xl leading-tight tracking-tight text-[color:var(--cn-ink)] sm:text-3xl">
          A wedding invitation should feel like paper and arrive like a message.
        </p>

        <div className="mt-12 flex flex-col gap-4 border-t border-[color:var(--cn-rule-2)] pt-6 text-sm sm:flex-row sm:items-center sm:justify-between">
          <BrandMark size="sm" className="text-[color:var(--cn-ink)]" />
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[color:var(--cn-muted)]">
            <span>© {copyrightYear} Goldypaper</span>
            <a href="/terms" target="_blank" rel="noopener" className={legalLinkClass}>
              Terms
            </a>
            <a href="/privacy" target="_blank" rel="noopener" className={legalLinkClass}>
              Privacy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function LandingShow() {
  const { signUpPath, googleAuthPath, copyrightYear, price, themes } =
    useContent<LandingContent>();

  return (
    /* shrink-0 is load-bearing: the shared shell scrolls a flex-column <main>
       (Layout.tsx), so this root is a flex item. With only min-h-screen it was
       shrunk to one viewport while its content ran on — the canvas painted the
       first fold and every section below it sat on the cream body background. */
    <div className="campaign-night campaign-canvas min-h-full shrink-0">
      <FloatingPill signUpPath={signUpPath} />
      <Hero
          signUpPath={signUpPath}
          googleAuthPath={googleAuthPath}
          price={price}
          themes={themes}
        />
      <ThemeShelf themes={themes} />
      <WhatItCovers price={price} signUpPath={signUpPath} />
      <HowItGoes />
      <FinalCta signUpPath={signUpPath} googleAuthPath={googleAuthPath} price={price} />
      <StatementFooter copyrightYear={copyrightYear} />
    </div>
  );
}
